/**
 * Copyright 2020 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import {
  getSearchProductDataFromCache,
  prefetchSearchProductDataInfinity,
  searchProductDataSelector,
} from '@/features/search/api/getSearchProductData';
import {
  prefetchSearchPartTypes,
  getSearchPartTypesFromCache,
} from '@/features/search/api/getSearchPartTypes';
import { SearchPageComponent } from '@/features/search/pages/SearchPageComponent';
import type { PageContextType } from '@/next-types';
import { configureSharedPageData } from '@/utils/configureSharedPageData.server';
import { dehydrate, type QueryClient } from '@tanstack/react-query';
import { getHeaderDataFromCache } from '@/features/header/api/getHeader';
import { getPreferredVehicle } from '@/features/header/utils/getPreferredVehicle';
import { getGlobalConfigFromCache, getFeatureFlag } from '@/features/globalConfig';
import Router from 'next/router';
import { getClientSideQueryClient } from '@/lib/react-query';
import root from 'window-or-global';
import { parseUrl, sanitizeUrlWithParamList } from '@/utils/urlHelpers';
import { acceptableShelfQueryParams } from '@/constants/queryParams';
import { countryCodes } from '@/constants/locale';
import { ProductListProvider } from '@/features/shelf/context/ProductListProvider';
import { ProductListViewProvider } from '@/features/shelf/context/ProductListViewProvider';
import { getMapFacet } from '@/hooks/useMapFacet';
import { type FacetModel } from '@/api/types/browse-search-types';
import environmentConfig from '@/config/environment';
import { type AxiosInstance } from 'axios';
import { getAxiosForClient } from '@/lib/axios';
import { getLocaleForProxiedHostname, getProxiedHostname } from '@/utils/getInitialAppState';
import { prefetchProductSkuDetails } from '@/features/product/api/getProductSkuDetails';
import { SisterStoreProvider } from '@/features/fulfillment/context/SisterStoreProvider';
import { getCookies } from '@/utils/cookie';
import { cookieConstant } from '@/constants/cookies';
import { is24ProductViewEnabled } from '@/hooks/useIs24ProductViewEnabled';
import { getDeviceType } from '@/utils/getDeviceType.server';
import type { AppState } from '@/stores/interface';
import { getKiboDecisionFlag } from '@/features/kibo/api/getKiboDecision';
import {
  getSearchSkuRecordDataFromCache,
  prefetchSearchSkuRecordData,
} from '@/features/search/api/getSearchRecordData';
import { prefetchContentStackProductListingPageData } from '@/features/productListingPage/api/getContentStackProductListingPageData';
import { productListingPageTypes } from '@/features/productListingPage/constants/productListingPageTypes';
import { handleLoadMoreRedirect } from '@/utils/common';
import { type NextApiRequest, type NextApiResponse } from 'next';
import { getSortValue } from '@/features/search/utils/getSortValue';
import { prefetchCitrusSearchCarouselData } from '@/features/citrus/api/getCitrusSearchCarouselData';
import { getCitrusSearchCarouselParams } from '@/features/citrus/utils/getCitrusSearchCarouselParams';
import { getSkuIdsForSearchPage } from '@/features/shelf/utils/getSkuIdsForSearchPage';
import { getCitrusShelfCarouselDataFromCache } from '@/features/citrus/api/getCitrusShelfCarouselData';
import { getConfigFlagsFromCache, prefetchConfigFlags } from '@/features/shelf/api/getConfigFlags';
import { type SearchSkuRecordResponse } from '@/features/search';
import { getAcesFlag } from '@/features/ymme/utils/acesFlags';
import { extractVehicleQuestions } from '@/features/header/utils/getVehicleQuestions';
import { addVehicleFromQueryParams } from '@/features/sharableLink/utils/addVehicleFromQueryParams';

export const config = {
  unstable_JsPreload: false,
};

function SearchResultsPage() {
  return (
    <SisterStoreProvider>
      <SearchPageComponent />
    </SisterStoreProvider>
  );
}

SearchResultsPage.getInitialProps = async (ctx: PageContextType) => {
  const { res, req, query, asPath } = ctx;

  if (!query.searchText) {
    if (__IS_SERVER__) {
      res?.writeHead(301, { Location: '/' }).end();
    } else {
      root.location.href = '/';
    }
    return {};
  }

  let queryClient: QueryClient;
  let initialAppState: AppState | undefined;
  let axios: AxiosInstance;
  const proxiedHostname = getProxiedHostname(req as NextApiRequest);
  const locale = getLocaleForProxiedHostname(proxiedHostname);

  if (__IS_SERVER__) {
    ({ axios, queryClient, initialAppState } = await configureSharedPageData(ctx));
  } else {
    axios = getAxiosForClient();
    queryClient = getClientSideQueryClient();
  }

  if (__IS_SERVER__) {
    const { acesEnabled } = getAcesFlag(queryClient);
    if (query.vehicleId || query.year) {
      await addVehicleFromQueryParams(queryClient, query, locale, axios, asPath, acesEnabled, {
        pageName: 'Search',
      });
    }
  }

  const headerData = getHeaderDataFromCache(queryClient);
  const vehicleQuestions = extractVehicleQuestions(
    headerData?.vehicleMap?.acesVehicleAttributes?.vehicleQuestionsIds
  ) as Record<string, string>;
  const vehicle = getPreferredVehicle(headerData);
  const globalConfig = getGlobalConfigFromCache(queryClient);
  const enable24ProductViewlagValue = globalConfig?.IS_24_PRODUCT_VIEW_ENABLED === 'true';

  const {
    searchText,
    currentPage,
    filters,
    vehicleSetFromSearch,
    recsPerPage,
    leftNavEnabled,
    partTypesView,
  } = query;

  const options = {
    searchText: typeof searchText === 'string' ? searchText : '',
    vehicleId: vehicle?.catalogVehicleId ?? '0',
    currentPage: typeof currentPage === 'string' ? currentPage : '1',
    filters: typeof filters === 'string' ? filters : undefined,
    leftNavEnabled: typeof leftNavEnabled === 'string' ? leftNavEnabled : undefined,
    vehicleSetFromSearch:
      typeof vehicleSetFromSearch === 'string' ? vehicleSetFromSearch : undefined,
    recsPerPage: typeof recsPerPage === 'string' ? recsPerPage : undefined,
  };

  let redirectLocation;
  if (__IS_SERVER__) {
    const isContentStackSRPEnabled = getKiboDecisionFlag(
      queryClient,
      'cmsSearchResultsPageEnabled'
    );
    handleLoadMoreRedirect(req as NextApiRequest, res as NextApiResponse);
    const isLoadMoreEnabled = getKiboDecisionFlag(queryClient, 'loadMoreEnabled');
    const citrusEnabled = globalConfig?.IS_CITRUS_ENABLED === 'true';
    const citrusSearchResultsSponsoredCardsEnabled =
      globalConfig?.IS_CITRUS_SEARCH_RESULTS_SPONSORED_CARDS_ENABLED === 'true';

    const { acesEnabled, acesNonVehicleEndpointsEnabled } = getAcesFlag(queryClient);

    let searchSkuRecordData: SearchSkuRecordResponse | undefined;

    if (isContentStackSRPEnabled && !!searchText) {
      const userSegments = headerData?.segments?.sort() ?? [];
      const searchSkuRecordOptions = {
        queryClient,
        locale,
        searchText: (searchText as string) ?? '',
        userSegments,
        vehicleId: vehicle?.catalogVehicleId ?? '0',
      };
      await prefetchSearchSkuRecordData({ axiosInstance: axios, ...searchSkuRecordOptions });

      searchSkuRecordData = getSearchSkuRecordDataFromCache(searchSkuRecordOptions);
      if (searchSkuRecordData?.skuRecord?.taxonomyPath) {
        await prefetchContentStackProductListingPageData({
          queryClient,
          url: searchSkuRecordData.skuRecord.taxonomyPath,
          context: ctx,
          productListingPageType: productListingPageTypes.searchResult,
          userSegments,
        });
      }

      if (searchSkuRecordData?.redirectUrl) {
        redirectLocation = searchSkuRecordData.redirectUrl;
      }
    }

    const deviceType = getDeviceType(req as NextApiRequest);
    const enable24ProductView = is24ProductViewEnabled({
      query,
      deviceType,
      flagValue: enable24ProductViewlagValue,
    });

    const minPrice = typeof query.minPrice === 'string' ? query.minPrice : undefined;
    const maxPrice = typeof query.maxPrice === 'string' ? query.maxPrice : undefined;

    const raSearchOptions = {
      searchText: options.searchText,
      vehicleId:
        acesEnabled && acesNonVehicleEndpointsEnabled
          ? options.vehicleId === '0'
            ? undefined
            : options.vehicleId
          : options.vehicleId,
      ...(!!vehicle &&
        acesEnabled &&
        acesNonVehicleEndpointsEnabled && {
          baseVehicle: {
            year: vehicle?.year,
            makeId: vehicle?.makeId,
            modelId: vehicle?.modelId,
            vehicleTypeId: vehicle?.vehicleTypeId,
          },
        }),
      ...(!!vehicleQuestions &&
        acesEnabled &&
        acesNonVehicleEndpointsEnabled && {
          vehicleQuestions,
        }),

      skipPdpRedirect: false,
      recordsPerPage:
        typeof options.recsPerPage === 'string'
          ? parseInt(options.recsPerPage)
          : enable24ProductView
          ? 24
          : undefined,
      pageNumber:
        partTypesView === 'true'
          ? typeof query?.currentPage === 'string'
            ? parseInt(query.currentPage)
            : 1
          : typeof query?.pageNumber === 'string'
          ? parseInt(query.pageNumber)
          : 1,
      locale,
      facet:
        typeof query.facet === 'string'
          ? minPrice || maxPrice
            ? `${query.facet ? `${query.facet}++` : ''}price:${minPrice ?? '*'} to ${
                maxPrice ?? '*'
              }`
            : query.facet
          : undefined,
      mapFacet: (facet: FacetModel) => getMapFacet(facet, 'Clearance'),
      sort: typeof query.sort === 'string' ? getSortValue(query.sort) : undefined,
      storeId: headerData?.storeNumber,
      userSegment: undefined,
      keywordSearchVisual: partTypesView === 'true' || undefined,
      preview: environmentConfig.SHOW_XM_PREVIEW_DATE,
      userProfileId: headerData?.myAccountMap?.profileId,
      eCookieId: initialAppState?.eCookieId ?? '',
      rewardsId: headerData?.myAccountMap?.rewardsId ?? '',
    };
    if (partTypesView === 'true') {
      await prefetchSearchPartTypes(queryClient, raSearchOptions, axios);
    } else {
      if (enable24ProductView || isLoadMoreEnabled) {
        raSearchOptions.recordsPerPage = 24;
      }
      await prefetchSearchProductDataInfinity(queryClient, raSearchOptions, axios, acesEnabled);
    }
    const searchPartTypesData = getSearchPartTypesFromCache(queryClient, raSearchOptions);

    const mapFacet = undefined;
    const searchProductData = searchProductDataSelector(
      getSearchProductDataFromCache(queryClient, raSearchOptions, acesEnabled) ?? {},
      mapFacet
    );

    if (searchPartTypesData?.redirectLocation) {
      redirectLocation = searchPartTypesData?.redirectLocation;
    } else if (searchProductData?.redirectUrl) {
      redirectLocation = searchProductData?.redirectUrl;
    }

    if (searchProductData?.records !== undefined && !redirectLocation) {
      if (searchSkuRecordData?.skuRecord?.eCommerceProductId) {
        await prefetchConfigFlags(
          { partNumber: searchSkuRecordData?.skuRecord?.eCommerceProductId },
          queryClient,
          axios
        );
      }

      const configFlagsData = getConfigFlagsFromCache(
        {
          partNumber: searchSkuRecordData?.skuRecord?.eCommerceProductId,
        },
        queryClient
      );

      const citrusSearchParams = getCitrusSearchCarouselParams({
        searchProductData,
        vehicle,
      });

      if (
        citrusEnabled &&
        citrusSearchResultsSponsoredCardsEnabled &&
        (configFlagsData?.IS_SPNSRD_ADVRTSMT_FL === 'false' ||
          (configFlagsData?.IS_SPNSRD_ADVRTSMT_FL === 'true' &&
            !headerData?.vehicleMap?.catalogVehicleId)) &&
        deviceType !== 'bot'
      ) {
        await prefetchCitrusSearchCarouselData(
          queryClient,
          ctx,
          citrusSearchParams,
          initialAppState?.eCookieId ?? ''
        );
      }

      const citrusSearchPageData = getCitrusShelfCarouselDataFromCache(
        citrusSearchParams,
        queryClient
      );

      const skuIds = getSkuIdsForSearchPage(
        searchProductData.records
          .map((record) => record.skuNumber.toString())
          .slice(0, enable24ProductView ? 12 : undefined),
        citrusSearchPageData
      );

      await prefetchProductSkuDetails(axios, queryClient!, {
        skuIds,
        storeNumber: headerData?.storeNumber ?? '',
        vehicleId: options.vehicleId,
        isNewDealsApiEnabled: getFeatureFlag(globalConfig, 'IS_NEW_DEALS_API_ENABLED') === 'true',
      });
    }
  }

  if (redirectLocation) {
    // We need to navigate using root.location instead of Router.push if
    // redirectLocation points to an external site or a page that isn't managed by Next.js.
    // This may be required by business in the Search API V2 in the future.
    if (redirectLocation.includes('http')) {
      if (__IS_SERVER__) {
        res
          ?.writeHead(301, {
            Location: redirectLocation,
          })
          .end();
        return;
      } else {
        const currentLocation = root.location.href ? root.location.href.toLowerCase() : '';

        if (currentLocation.includes('/searchresult?searchtext=rebate')) {
          root.location.replace(redirectLocation);
        } else {
          root.location.assign(redirectLocation);
        }
        return;
      }
    }

    const { url, query: parseUrlQuery } = parseUrl(redirectLocation);
    /**
     * currenly we really only have requirements around shelf. the whitelisted params here may
     * need to be updated in the future for other use cases
     */

    const sanitizedRedirectPath = sanitizeUrlWithParamList(
      parseUrlQuery,
      acceptableShelfQueryParams,
      url
    );
    const redirectURL = new URL(
      sanitizedRedirectPath,
      `https://${__IS_SERVER__ && req ? proxiedHostname : window.location.hostname}`
    );
    redirectURL.searchParams.set('searchText', typeof searchText === 'string' ? searchText : '');

    const redirectPath = redirectURL.pathname + redirectURL.search;

    return __IS_SERVER__
      ? res?.writeHead(301, { Location: redirectPath }).end()
      : Router.push(redirectPath);
  }

  if (__IS_SERVER__) {
    let isListView =
      globalConfig?.IS_DEFAULT_GRID_VIEW_ENABLED === 'true'
        ? false
        : initialAppState?.locale !== countryCodes.mx;
    const cookies = getCookies({ req: req as NextApiRequest, res });
    const shelfListGridViewCookie = cookies[cookieConstant.names.shelfListGridView];

    if (shelfListGridViewCookie) {
      isListView = shelfListGridViewCookie !== cookieConstant.values.shelfListGridView;
    }

    return {
      initialAppState,
      isListView,
      dehydratedState: dehydrate(queryClient),
    };
  }

  return {};
};
SearchResultsPage.getLayout = (
  page: React.ReactElement,
  pageProps: { isListView?: boolean; initialAppState: AppState }
) => {
  let isListView = true;

  if (
    'initialAppState' in pageProps &&
    'IS_DEFAULT_GRID_VIEW_ENABLED' in pageProps.initialAppState
  ) {
    isListView = !pageProps.initialAppState.IS_DEFAULT_GRID_VIEW_ENABLED;
  }

  if ('isListView' in pageProps && pageProps.isListView != null) {
    isListView = pageProps.isListView;
  }

  if ('initialAppState' in pageProps && pageProps.initialAppState.locale === 'es-MX') {
    isListView = pageProps.initialAppState.locale !== 'es-MX';
  }

  if ('initialAppState' in pageProps && pageProps.initialAppState.locale === countryCodes.ptBr) {
    isListView = false;
  }

  return (
    <ProductListProvider>
      <ProductListViewProvider isListView={isListView}>{page}</ProductListViewProvider>
    </ProductListProvider>
  );
};

export default SearchResultsPage;
